import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ChartData, ChartOptions } from 'chart.js';
import { Subject, timeout } from 'rxjs';
import { ClientAnalyticsControllerService, ClientBrandTagResponse, ClientCoverageAnalysisRequest, ClientCoverageAnalysisResponse } from 'api-clipping';
import { UtilsService } from '../../../../shared/services/utils.service';
import { ChartComponent } from '../../../../shared/components/sette/chart/chart.component';
import { KpiTileComponent } from '../../../../shared/components/sette/kpi-tile/kpi-tile.component';
import { SampleSizeNoteComponent } from '../../../../shared/components/sette/sample-size-note/sample-size-note.component';
import { AnalyticsSkeletonComponent } from '../../../../shared/components/sette/analytics-skeleton/analytics-skeleton.component';
import { AnalyticsErrorStateComponent, AnalyticsErrorKind } from '../../../../shared/components/sette/analytics-error-state/analytics-error-state.component';
import { AnalyticsEmptyStateComponent } from '../../../../shared/components/sette/analytics-empty-state/analytics-empty-state.component';
import { InfoIconComponent } from '../../../../shared/components/sette/info-icon/info-icon.component';
import { FilterState } from '../filter-bar/filter-bar.component';
import { buildAnalyticsFilterRequest, isCompareRequested, resolveComparison, classifyAnalyticsError, createAnalyticsFetchStream, ANALYTICS_REQUEST_TIMEOUT_MS, noMentionsLabel, isNewMetric, newMetricLabel } from '../shared/analytics-request.util';
import { signTone, formatSigned, formatSignedAuto, formatAnalyticsNumber, formatAnalyticsInt, formatAnalyticsAxisDate, seqTextColor, categoricalColors, brandColor, MEDIA_COLORS, SENTIMENT_COLORS, AXIS, Tone } from '../../../../shared/components/sette/chart-theme';
import { translateTopic } from '../shared/taxonomy.util';

export type AnalysisMetric = 'Mentions' | 'Reach' | 'Impact';

interface BrandRowView {
	brand: string;
	volume: number;
	sov: string;
	reach: number;
	impact: number;
	impactStr: string;
	impactTone: Tone;
	color: string;
}

interface MediaTypeRowView {
	name: string;
	volume: number;
	percentage: string;
	reach: number;
	impact: number;
	impactStr: string;
	impactTone: Tone;
	color: string;
}

interface TopMediaRowView {
	name: string;
	type: string;
	mentions: number;
	reach: string;
	impactStr: string;
	impactTone: Tone;
}

interface TopicRowView {
	name: string;
	volume: number;
	percentage: string;
	reach: string;
	reachValue: number;
	impact: number;
	impactStr: string;
	impactTone: Tone;
	color: string;
	barWidth: number;
}

interface TopAuthorRowView {
	author: string;
	media: string;
	volume: number;
	reach: string;
	impactStr: string;
	impactTone: Tone;
}

interface SentimentSplitRowView {
	brand: string;
	pos: number;
	neu: number;
	neg: number;
}

interface ReachTierRowView {
	tier: string;
	mentions: number;
	percentage: string;
	color: string;
}

interface ScatterPointView {
	reach: number;
	impact: number;
	impactTone: Tone;
	headline: string;
	outlet: string;
	brand: string;
	date: string;
}

interface KpiView {
	value: string;
	trendUp?: boolean;
	deltaLabel: string;
}

// SETTE redesign (CLIPPING-598): see the matching comment in coverage-dashboard.component.ts -
// brand/topic rows get the categorical palette (avoiding chart-5), media type gets MEDIA_COLORS.
const PALETTE = categoricalColors(8, { avoidSentimentClash: true });
// Backend media-type codes -> the same ANALYTICS_FILTER_BAR.MEDIA_TYPE_* keys the Filters drawer
// already uses for these exact labels (Web Portals/Television/Print Press/Radio/Social Media).
const MEDIA_TYPE_LABEL_KEYS: Record<string, string> = {
	WEB: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_WEB',
	TV: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_TV',
	PRINTED: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_PRINT',
	RADIO: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_RADIO',
	SOCIAL: 'ANALYTICS_FILTER_BAR.MEDIA_TYPE_SOCIAL',
};
const MEDIA_TYPE_COLORS: Record<string, string> = { WEB: MEDIA_COLORS.portal, TV: MEDIA_COLORS.tv, PRINTED: MEDIA_COLORS.print, RADIO: MEDIA_COLORS.radio, SOCIAL: '#7D8796' };

// Ported from analytics-design/src/components/AnalysisDashboard.tsx, then rewired to call
// POST /v1/client/analytics/coverage-analysis instead of using sample data. The design's own
// per-metric KPI row (12 distinct cards across the 3 tabs: Peak Day, Highest Reach Day, Net
// Impact Lead, ...) needed data this backend doesn't compute (per-day peaks, named cross-brand
// deltas) - the KPI row is now the same 4 metrics every other screen on this branch uses
// (Mentions/Avg Reach/Avg Impact/Share of Voice), not swapped per tab. "Reach x Sentiment per
// Brand" (reach score split by sentiment, Reach tab) has no backend equivalent either and was
// dropped; "Reach Distribution by Tier" on the same tab is real (reachTiers) and kept. Everything
// else - the trend, By Brand, By Media Type, Top Media, Industry/Corporate Topics, Top Authors,
// Sentiment Split per Brand, Coverage Calendar, and the Impact scatter plot (now up to 300 real
// articles instead of 240 synthetic/random ones) - is real and still switches with the
// Mentions/Reach/Impact segmented control exactly as before.
@Component({
	selector: 'app-coverage-analysis',
	standalone: true,
	imports: [MatIconModule, TranslateModule, ChartComponent, KpiTileComponent, SampleSizeNoteComponent, AnalyticsSkeletonComponent, AnalyticsErrorStateComponent, AnalyticsEmptyStateComponent, InfoIconComponent],
	templateUrl: './coverage-analysis.component.html',
	styleUrl: './coverage-analysis.component.scss',
})
export class CoverageAnalysisComponent implements OnInit, OnChanges {
	@Input() initialMetric: AnalysisMetric = 'Mentions';
	@Input() filters!: FilterState;
	@Input() brandOptions: ClientBrandTagResponse[] = [];
	@Output() navigate = new EventEmitter<string>();
	// Lets AnalyticsPanelComponent track which tab is actually showing (not just the initial seed
	// from initialMetric) - needed so Save View captures the real current tab.
	@Output() metricChange = new EventEmitter<AnalysisMetric>();

	metric: AnalysisMetric = 'Mentions';
	readonly metricOptions: AnalysisMetric[] = ['Mentions', 'Reach', 'Impact'];

	loading = false;
	loadError = false;
	errorState: AnalyticsErrorKind = { kind: 'generic' };
	data: ClientCoverageAnalysisResponse | null = null;
	private readonly fetchTrigger = new Subject<ClientCoverageAnalysisRequest>();

	constructor(private readonly api: ClientAnalyticsControllerService, private readonly utils: UtilsService, private readonly translate: TranslateService) {
		// Задача 7: switchMap-based stale-response guard - see createAnalyticsFetchStream's doc.
		createAnalyticsFetchStream(this.fetchTrigger, (request) => this.api.getCoverageAnalysisAnalytics(request).pipe(timeout(ANALYTICS_REQUEST_TIMEOUT_MS)))
			.pipe(takeUntilDestroyed())
			.subscribe((result) => {
				this.loading = false;
				if (result.ok) {
					this.data = result.data!;
				} else {
					this.loadError = true;
					this.errorState = classifyAnalyticsError(result.error);
				}
			});
	}

	get lang(): string {
		return this.utils.currentLanguage;
	}

	fmtInt(n: number): string {
		return formatAnalyticsInt(n, this.lang);
	}

	fmt1(n: number): string {
		return formatAnalyticsNumber(n, this.lang, 1);
	}

	get showSkeleton(): boolean {
		return this.loading && !this.data;
	}

	get isRefreshing(): boolean {
		return this.loading && !!this.data;
	}

	get isEmptyPeriod(): boolean {
		return !this.loading && !this.loadError && (this.data?.mentions?.value ?? 0) === 0;
	}

	ngOnInit(): void {
		this.metric = this.initialMetric;
		this.metricChange.emit(this.metric);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['filters']) {
			this.fetch();
		}
	}

	fetch(): void {
		const fields = buildAnalyticsFilterRequest(this.filters);
		if (!fields) {
			return;
		}
		this.loading = true;
		this.loadError = false;
		this.fetchTrigger.next(fields as ClientCoverageAnalysisRequest);
	}

	setMetric(metric: AnalysisMetric): void {
		this.metric = metric;
		this.metricChange.emit(metric);
	}

	// "Reach" -> "Visibility" in all copy (SETTE redesign, CLIPPING-598) - this is the one place
	// that mapping happens for every template spot that shows the active tab's name.
	metricLabel(metric: AnalysisMetric = this.metric): string {
		const key = metric === 'Reach' ? 'ANALYTICS_GLOSSARY.VISIBILITY' : metric === 'Impact' ? 'ANALYTICS_GLOSSARY.IMPACT' : 'ANALYTICS_GLOSSARY.MENTIONS';
		return this.translate.instant(key);
	}

	mediaTypeLabel(code: string): string {
		const key = MEDIA_TYPE_LABEL_KEYS[code];
		return key ? this.translate.instant(key) : code;
	}

	onNavigate(targetId: string): void {
		this.navigate.emit(targetId);
	}

	toneClasses(tone: Tone): string {
		if (tone === 'positive') return 'bg-positive-tint text-positive';
		if (tone === 'negative') return 'bg-negative-tint text-negative';
		return 'bg-neutral-tint text-ink-subtle';
	}

	// --- KPI row (same 4 metrics on every tab) ---

	private kpi(
		value: number | undefined,
		previousValue: number | undefined | null,
		hasEnoughCompareData: boolean | undefined,
		format: (n: number) => string,
		deltaSuffix: 'pts' | '%' | 'pp'
	): KpiView {
		// Analytics accuracy review follow-up (2026-11-01): never hide a number for being small -
		// only a genuinely empty period (0 mentions, value null) has nothing to show at all.
		if (value == null) {
			return { value: '—', deltaLabel: noMentionsLabel(this.translate) };
		}
		const comparison = resolveComparison(isCompareRequested(this.filters), previousValue, hasEnoughCompareData);
		if (comparison.insufficient) {
			// No usable previous value at all (the compare period had 0 mentions) - "Ново" if this
			// period has a real value to report, otherwise nothing to say.
			return { value: format(value), deltaLabel: isNewMetric(value, 0) ? newMetricLabel(this.translate) : '' };
		}
		if (!comparison.show) {
			return { value: format(value), deltaLabel: '' };
		}
		const prev = comparison.previous as number;
		const delta = value - prev;
		const deltaLabel =
			deltaSuffix === '%' && isNewMetric(value, prev)
				? newMetricLabel(this.translate)
				: deltaSuffix === '%'
					? `${delta >= 0 ? '+' : '-'}${formatAnalyticsInt(Math.abs(prev !== 0 ? Math.round((delta / prev) * 100) : 0), this.lang)}%`
					: `${delta >= 0 ? '+' : '-'}${formatAnalyticsNumber(Math.abs(delta), this.lang, 1)} ${deltaSuffix}`;
		return { value: format(value), trendUp: value >= prev, deltaLabel };
	}

	get mentionsKpi(): KpiView {
		return this.kpi(this.data?.mentions?.value, this.data?.mentions?.previousValue, this.data?.mentions?.hasEnoughCompareData, (n) => formatAnalyticsInt(Math.round(n), this.lang), '%');
	}

	get avgReachKpi(): KpiView {
		return this.kpi(this.data?.avgReach?.value, this.data?.avgReach?.previousValue, this.data?.avgReach?.hasEnoughCompareData, (n) => `${formatAnalyticsNumber(n, this.lang, 1)}/100`, 'pts');
	}

	get avgImpactKpi(): KpiView {
		return this.kpi(this.data?.avgImpact?.value, this.data?.avgImpact?.previousValue, this.data?.avgImpact?.hasEnoughCompareData, (n) => (n >= 0 ? '+' : '-') + formatAnalyticsNumber(n, this.lang, 1), 'pts');
	}

	get shareOfVoiceKpi(): KpiView {
		return this.kpi(this.data?.shareOfVoice?.value, this.data?.shareOfVoice?.previousValue, this.data?.shareOfVoice?.hasEnoughCompareData, (n) => `${formatAnalyticsNumber(n, this.lang, 1)}%`, 'pp');
	}

	// "Врз основа на N спомнувања" caption under the Trend chart and KPI cards - follows the
	// active tab, same sampleSize already backing that metric's own KPI card.
	get trendSampleSize(): number | undefined {
		if (this.metric === 'Mentions') return this.data?.mentions?.sampleSize;
		if (this.metric === 'Reach') return this.data?.avgReach?.sampleSize;
		return this.data?.avgImpact?.sampleSize;
	}

	// --- Trend chart (single series, follows the selected metric) ---

	// SETTE redesign (CLIPPING-598), Правило А: a single-series trend is always chart-1 blue,
	// never green - Mentions/Reach(Visibility) have no sign so they stay blue regardless of tab;
	// Impact is signed, so the line stays blue too and the sign lives on each point instead.
	get trendChartData(): ChartData {
		const trend = this.data?.trend ?? [];
		const labels = trend.map((t) => (t.bucketStart ? formatAnalyticsAxisDate(new Date(t.bucketStart), this.lang) : ''));
		const values = trend.map((t) => (this.metric === 'Mentions' ? t.mentions ?? 0 : this.metric === 'Reach' ? t.avgReach ?? null : t.avgImpact ?? null));
		const pointColors =
			this.metric === 'Impact' ? trend.map((t) => SENTIMENT_COLORS[signTone(t.avgImpact ?? 0)]) : '#244896';
		return { labels, datasets: [{ label: this.metricLabel(), data: values, borderColor: '#244896', backgroundColor: '#244896', borderWidth: 2.4, pointRadius: 3, pointBackgroundColor: pointColors, tension: 0.4, spanGaps: true }] };
	}

	get trendChartOptions(): ChartOptions {
		const tooltip = { backgroundColor: AXIS.tooltipBg, titleColor: '#ffffff', bodyColor: '#ffffff', borderWidth: 0, cornerRadius: 10, bodyFont: { size: 11 }, titleFont: { size: 11 } };
		if (this.metric === 'Impact') {
			return {
				responsive: true,
				maintainAspectRatio: false,
				interaction: { mode: 'index', intersect: false },
				plugins: { legend: { display: false }, tooltip: { ...tooltip, callbacks: { label: (item: any) => formatSignedAuto(Number(item.raw), this.lang) } } },
				scales: { x: { grid: { display: false }, ticks: { color: AXIS.text, font: { size: 10 } } }, y: { min: -100, max: 100, ticks: { color: AXIS.text, font: { size: 10 }, callback: (v) => formatSignedAuto(Number(v), this.lang) }, grid: { color: AXIS.grid } } },
			};
		}
		return {
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: 'index', intersect: false },
			plugins: { legend: { display: false }, tooltip: this.metric === 'Reach' ? { ...tooltip, callbacks: { label: (item: any) => `${formatAnalyticsNumber(item.raw, this.lang, 1)}/100` } } : tooltip },
			scales: {
				x: { grid: { display: false }, ticks: { color: AXIS.text, font: { size: 10 } } },
				y: this.metric === 'Reach' ? { min: 0, max: 100, ticks: { stepSize: 25, color: AXIS.text, font: { size: 10 } }, grid: { color: AXIS.grid } } : { ticks: { color: AXIS.text, font: { size: 10 } }, grid: { color: AXIS.grid } },
			},
		};
	}

	// --- By Brand ---

	get sortedBrandList(): BrandRowView[] {
		const rows: BrandRowView[] = (this.data?.brands ?? []).map((b) => ({
			brand: b.name || '',
			volume: b.mentions ?? 0,
			sov: `${formatAnalyticsNumber(b.sharePercent ?? 0, this.lang, 1)}%`,
			reach: b.avgReach ?? 0,
			impact: b.avgImpact ?? 0,
			impactStr: b.avgImpact != null ? formatSigned(b.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(b.avgImpact ?? 0),
			color: brandColor(b.tagId, this.brandOptions),
		}));
		if (this.metric === 'Mentions') return [...rows].sort((a, b) => b.volume - a.volume);
		if (this.metric === 'Reach') return [...rows].sort((a, b) => b.reach - a.reach);
		return [...rows].sort((a, b) => b.impact - a.impact);
	}

	get maxBrandVolume(): number {
		return Math.max(1, ...this.sortedBrandList.map((b) => b.volume));
	}

	divergingPercent(impact: number): number {
		return Math.min(100, (Math.abs(impact) / 40) * 100);
	}

	get brandFooterLine(): string {
		const top = this.sortedBrandList[0];
		if (!top) return '';
		if (this.metric === 'Mentions') return this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.BRAND_FOOTER_MENTIONS', { brand: top.brand, sov: top.sov });
		if (this.metric === 'Reach') return this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.BRAND_FOOTER_VISIBILITY', { brand: top.brand, score: formatAnalyticsNumber(top.reach, this.lang, 1) });
		return this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.BRAND_FOOTER_IMPACT', { brand: top.brand, impact: top.impactStr });
	}

	// --- By Media Type ---

	get mediaTypeData(): MediaTypeRowView[] {
		const rows = this.data?.mediaTypes ?? [];
		const total = rows.reduce((s, r) => s + (r.mentions ?? 0), 0);
		return rows.map((r, i) => ({
			name: this.mediaTypeLabel(r.type || ''),
			volume: r.mentions ?? 0,
			percentage: total > 0 ? `${formatAnalyticsNumber(((r.mentions ?? 0) / total) * 100, this.lang, 1)}%` : '0%',
			reach: r.avgReach ?? 0,
			impact: r.avgImpact ?? 0,
			impactStr: r.avgImpact != null ? formatSigned(r.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(r.avgImpact ?? 0),
			color: MEDIA_TYPE_COLORS[r.type || ''] || '#7D8796',
		}));
	}

	get mediaTypeTotal(): number {
		return this.mediaTypeData.reduce((s, r) => s + r.volume, 0);
	}

	get sortedMediaTypeData(): MediaTypeRowView[] {
		const list = [...this.mediaTypeData];
		if (this.metric === 'Mentions') return list.sort((a, b) => b.volume - a.volume);
		if (this.metric === 'Reach') return list.sort((a, b) => b.reach - a.reach);
		return list.sort((a, b) => b.impact - a.impact);
	}

	get mediaTypeChartData(): ChartData {
		return { labels: this.mediaTypeData.map((m) => m.name), datasets: [{ data: this.mediaTypeData.map((m) => m.volume), backgroundColor: this.mediaTypeData.map((m) => m.color), borderWidth: 0, spacing: 2 }] };
	}

	readonly mediaTypeChartOptions = { responsive: true, maintainAspectRatio: false, cutout: '69%', plugins: { legend: { display: false }, tooltip: { enabled: false } } };

	get mediaTypeFooterLine(): string {
		const top = this.sortedMediaTypeData[0];
		if (!top) return '';
		if (this.metric === 'Mentions') return this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.MEDIA_TYPE_FOOTER_MENTIONS', { name: top.name, percentage: top.percentage });
		if (this.metric === 'Reach') return this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.MEDIA_TYPE_FOOTER_VISIBILITY', { name: top.name, score: formatAnalyticsNumber(top.reach, this.lang, 1) });
		return this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.MEDIA_TYPE_FOOTER_IMPACT', { name: top.name, impact: top.impactStr });
	}

	// --- Top media outlets ---

	get sortedTopMediaTop6(): TopMediaRowView[] {
		const rows: TopMediaRowView[] = (this.data?.topMedia ?? []).map((r) => ({
			name: r.name || '',
			type: this.mediaTypeLabel(r.type || ''),
			mentions: r.mentions ?? 0,
			reach: r.avgReach != null ? `${formatAnalyticsNumber(r.avgReach, this.lang, 1)}/100` : '—',
			impactStr: r.avgImpact != null ? formatSigned(r.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(r.avgImpact ?? 0),
		}));
		return rows.slice(0, 6);
	}

	// --- Industry / Corporate topics ---

	private topicRows(source: 'industryTopics' | 'corporateTopics'): TopicRowView[] {
		const rows = this.data?.[source] ?? [];
		const maxVolume = Math.max(1, ...rows.map((t) => t.mentions ?? 0));
		const list: TopicRowView[] = rows.map((t, i) => ({
			name: translateTopic(t.topic, this.translate),
			volume: t.mentions ?? 0,
			percentage: t.sharePercent != null ? `${formatAnalyticsNumber(t.sharePercent, this.lang, 1)}%` : '0%',
			reach: t.avgReach != null ? formatAnalyticsNumber(t.avgReach, this.lang, 1) : '—',
			reachValue: t.avgReach ?? 0,
			impact: t.avgImpact ?? 0,
			impactStr: t.avgImpact != null ? formatSigned(t.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(t.avgImpact ?? 0),
			color: PALETTE[i % PALETTE.length],
			barWidth: this.metric === 'Mentions' ? ((t.mentions ?? 0) / maxVolume) * 100 : this.metric === 'Reach' ? (t.avgReach ?? 0) : Math.min(100, Math.abs(t.avgImpact ?? 0) * 3),
		}));
		if (this.metric === 'Mentions') return list.sort((a, b) => b.volume - a.volume);
		if (this.metric === 'Reach') return list.sort((a, b) => b.reachValue - a.reachValue);
		return list.sort((a, b) => b.impact - a.impact);
	}

	get sortedIndustryTopics(): TopicRowView[] {
		return this.topicRows('industryTopics');
	}

	get sortedCorporateTopics(): TopicRowView[] {
		return this.topicRows('corporateTopics');
	}

	// SETTE redesign (CLIPPING-598), Правило А: rank-lists of topics/categories are all blue on
	// Mentions/Reach (no sign = no color-by-category); only the Impact tab diverges by sign.
	topicBarColor(t: TopicRowView): string {
		if (this.metric === 'Impact') return SENTIMENT_COLORS[t.impactTone];
		return '#244896';
	}

	// --- Top authors ---

	get sortedTopAuthorsTop5(): TopAuthorRowView[] {
		const rows: TopAuthorRowView[] = (this.data?.topAuthors ?? []).map((a) => ({
			author: a.name || '',
			media: a.mediumName || '',
			volume: a.mentions ?? 0,
			reach: a.avgReach != null ? formatAnalyticsNumber(a.avgReach, this.lang, 1) : '—',
			impactStr: a.avgImpact != null ? formatSigned(a.avgImpact, 1, this.lang) : '—',
			impactTone: signTone(a.avgImpact ?? 0),
		}));
		return rows.slice(0, 5);
	}

	// --- Mentions tab bottom block: sentiment split + calendar ---

	get sentimentSplitData(): SentimentSplitRowView[] {
		return (this.data?.sentimentByBrand ?? []).map((b) => ({
			brand: b.name || '',
			pos: Math.round(b.positivePercent ?? 0),
			neu: Math.round(b.neutralPercent ?? 0),
			neg: Math.round(b.negativePercent ?? 0),
		}));
	}

	get calendarLeadingBlanks(): number[] {
		const first = this.data?.calendar?.[0];
		if (!first?.date) return [];
		const weekday = new Date(first.date).getDay();
		return Array.from({ length: (weekday + 6) % 7 });
	}

	get calendarDays(): { day: number; date: string; intensity: number }[] {
		return (this.data?.calendar ?? []).map((d) => {
			const mentions = d.mentions ?? 0;
			const intensity = mentions >= 5 ? 5 : mentions === 4 ? 4 : mentions >= 2 ? 3 : mentions === 1 ? 2 : 1;
			return { day: d.date ? new Date(d.date).getDate() : 0, date: d.date || '', intensity };
		});
	}

	getCalendarBg(intensity: number): string {
		if (intensity === 5) return `bg-seq-5 ${seqTextColor(4)} font-medium`;
		if (intensity === 4) return `bg-seq-4 ${seqTextColor(3)} font-medium`;
		if (intensity === 3) return `bg-seq-3 ${seqTextColor(2)}`;
		if (intensity === 2) return `bg-seq-2 ${seqTextColor(1)}`;
		return 'bg-page text-ink-subtle';
	}

	// --- Reach tab bottom block: reach tiers ---

	get reachTierData(): ReachTierRowView[] {
		const colors = ['#244896', '#4a6fb8', '#7d99d3', '#b9c9ea'];
		return (this.data?.reachTiers ?? []).map((t, i) => ({
			tier: t.tier || '',
			mentions: t.mentions ?? 0,
			percentage: t.sharePercent != null ? `${formatAnalyticsInt(t.sharePercent, this.lang)}%` : '0%',
			color: colors[i % colors.length],
		}));
	}

	// --- Impact tab bottom block: scatter ---

	get scatterPoints(): ScatterPointView[] {
		return (this.data?.scatterPoints ?? [])
			.filter((s) => s.reach != null && s.impact != null)
			.map((s) => ({
				reach: s.reach ?? 0,
				impact: s.impact ?? 0,
				impactTone: signTone(s.impact ?? 0),
				headline: s.headline || '',
				outlet: s.outletName || '',
				brand: s.brandTagName || '',
				date: s.publicationDate ? formatAnalyticsAxisDate(new Date(s.publicationDate), this.lang) : '',
			}));
	}

	get scatterChartData() {
		const points = this.scatterPoints;
		return {
			datasets: [
				{
					label: this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.SCATTER_ARTICLES_DATASET'),
					data: points.map((p) => ({ x: p.reach, y: p.impact })),
					pointBackgroundColor: points.map((p) => SENTIMENT_COLORS[p.impactTone]),
					pointRadius: 4,
					pointHoverRadius: 6,
					pointBorderWidth: 0,
				},
				{
					type: 'line' as const,
					label: this.translate.instant('ANALYTICS_COVERAGE_ANALYSIS.ZERO_BASELINE_DATASET'),
					data: [
						{ x: 0, y: 0 },
						{ x: 100, y: 0 },
					],
					borderColor: AXIS.baseline,
					borderDash: [3, 3],
					borderWidth: 1.5,
					pointRadius: 0,
					pointHitRadius: 0,
				},
			],
		};
	}

	get scatterChartOptions(): ChartOptions {
		const points = this.scatterPoints;
		return {
			responsive: true,
			maintainAspectRatio: false,
			plugins: {
				legend: { display: false },
				tooltip: {
					backgroundColor: AXIS.tooltipBg,
					titleColor: '#ffffff',
					bodyColor: '#ffffff',
					borderWidth: 0,
					cornerRadius: 10,
					padding: 10,
					filter: (item: any) => item.datasetIndex === 0,
					callbacks: {
						title: (items: any[]) => points[items[0].dataIndex]?.headline ?? '',
						label: (item: any) => {
							const p = points[item.dataIndex];
							if (!p) return '';
							return [
								`${p.outlet} • ${p.brand} • ${p.date}`,
								`${this.translate.instant('ANALYTICS_GLOSSARY.VISIBILITY')}: ${formatAnalyticsNumber(p.reach, this.lang, 1)}/100   ${this.translate.instant('ANALYTICS_GLOSSARY.IMPACT')}: ${formatSignedAuto(p.impact, this.lang)} pts`,
							];
						},
					},
				},
			},
			scales: {
				x: { type: 'linear', min: 0, max: 100, ticks: { stepSize: 20, color: AXIS.text, font: { size: 11 } }, grid: { color: AXIS.grid }, title: { display: true, text: this.translate.instant('ANALYTICS_GLOSSARY.VISIBILITY'), color: AXIS.text, font: { size: 11 } } },
				y: { type: 'linear', min: -100, max: 100, ticks: { stepSize: 50, color: AXIS.text, font: { size: 11 }, callback: (v) => formatSignedAuto(Number(v), this.lang) }, grid: { color: AXIS.grid }, title: { display: true, text: this.translate.instant('ANALYTICS_GLOSSARY.IMPACT'), color: AXIS.text, font: { size: 11 } } },
			},
		};
	}
}
